/**
 * Display aggregate statistics across all confirmed show dates
 * Matches legacy layout with equation-style presentation
 */

import React from 'react';
import { Card, CardContent, Typography, Stack, Box } from '@mui/material';
import type { HoldList, Hold } from 'api-client/models';
import { formatCurrency } from '../../../utils/formatters';

interface HoldAggregateStatsProps {
  holds: HoldList[];
  fullHoldDetails: Record<string, Hold>;
}

export const HoldAggregateStats: React.FC<HoldAggregateStatsProps> = ({ holds, fullHoldDetails }) => {
  const confirmedHolds = holds.filter((h) => h.status === 'confirmed');

  // List financials cover every date; details load only for the selected hold.
  const totalRevenue = confirmedHolds.reduce(
    (sum, hold) => sum + parseFloat(hold.totalRevenue || '0'),
    0,
  );

  const netProfit = confirmedHolds.reduce(
    (sum, hold) => sum + parseFloat(hold.netProfit || '0'),
    0,
  );

  const totalExpenses = totalRevenue - netProfit;

  // Partial detail loading cannot establish a total ticket count or dollars per head.
  const allDetailsLoaded =
    confirmedHolds.length > 0 && confirmedHolds.every((hold) => fullHoldDetails[hold.id]);
  const totalTicketsSold = confirmedHolds.reduce((sum, hold) => {
    const holdDetails = fullHoldDetails[hold.id];
    return sum + (holdDetails?.ticketsSold || 0);
  }, 0);
  const dollarsPerHead = totalTicketsSold > 0 ? totalRevenue / totalTicketsSold : 0;

  if (confirmedHolds.length === 0) {
    return null;
  }

  return (
    <Card variant="outlined" sx={{ mt: 2, bgcolor: 'action.hover' }}>
      <CardContent sx={{ py: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Show Totals (All Dates Combined)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Ticket counts load as dates are selected. Ticket totals and dollars per head remain unknown until every confirmed date is loaded.
        </Typography>
        
        <Stack spacing={3}>
          {/* First Row: Tickets and Dollars per Head */}
          <Stack direction="row" spacing={3} useFlexGap flexWrap="wrap">
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Tickets Sold
              </Typography>
              <Typography variant="h6" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                {allDetailsLoaded ? totalTicketsSold : '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Dollars per Head
              </Typography>
              <Typography variant="h6" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                {allDetailsLoaded ? formatCurrency(dollarsPerHead) : '—'}
              </Typography>
            </Box>
          </Stack>

          {/* Second Row: Math Equation Style */}
          <Stack direction="row" spacing={2} useFlexGap alignItems="center" flexWrap="wrap"
            sx={{ '& .MuiTypography-h6': { whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' } }}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Revenue
              </Typography>
              <Typography variant="h6" sx={{ color: 'statusText.ready' }}>
                {formatCurrency(totalRevenue)}
              </Typography>
            </Box>

            <Typography variant="h6" sx={{ alignSelf: 'flex-end', pb: 0.5 }}>
              −
            </Typography>

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Expenses
              </Typography>
              <Typography variant="h6" sx={{ color: 'statusText.attention' }}>
                {formatCurrency(totalExpenses)}
              </Typography>
            </Box>

            <Typography variant="h6" sx={{ alignSelf: 'flex-end', pb: 0.5 }}>
              =
            </Typography>

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Net Profit
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: netProfit >= 0 ? 'statusText.ready' : 'statusText.attention',
                  fontWeight: 'bold',
                }}
              >
                {formatCurrency(netProfit)}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};
